import React, { useEffect, useMemo, useState } from 'react';
import { theme, Select } from 'components';
import { debounce } from 'lodash';
import styled from 'styled-components';
import Axios from 'axios';
import { Loading } from '@app/react-common';
import { featureEnabled } from 'utils';
import SearchLabel from './SearchLabel';

type SearchBarProps = {
  searchLabel?: 'owner' | 'assignee';
  disabled?: boolean;
  isMlsUser: boolean;
  value?: string;
  defaultType?: {
    label: string;
    value: 'office' | 'user';
  };
  resetToDefault?: boolean;
  onValueChange: (data: string | undefined) => void;
  assignmentType?: 'owner' | 'assignee' | 'ownerAndAssignee';
  style?: React.CSSProperties;
};

const KeyholderSearch: React.FC<SearchBarProps> = ({
  searchLabel,
  disabled,
  isMlsUser,
  value,
  onValueChange,
  defaultType,
  assignmentType,
  resetToDefault,
  style,
}) => {
  const [selectValue, setSelectValue] = useState<string | undefined>(value);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>([]);

  useEffect(() => {
    setSelectValue(value);
  }, [value]);

  // Helper method to determine the default type for the dropdown.
  // If the Assignment Type is 'Owner and Assignee' then the Assignee should only have 'User' as a selectable option
  // However, in Mongo, the default assignee might be set to 'branch' (aka office), so in this case the default value should be None (aka '')
  // [OB-531] Branch should be renamed to office entirely here
  const defaultTypeHelper = React.useCallback(() => {
    if (defaultType?.value === 'office') {
      return assignmentType === 'ownerAndAssignee' && searchLabel === 'assignee'
        ? ''
        : 'office';
    }
    return defaultType?.value || '';
  }, [defaultType, assignmentType, searchLabel]);

  // [OB-541] Remove 'brokerage' option
  // [OB-531] Branch should be renamed to office entirely here
  const [type, setType] = useState<
    'user' | 'branch' | 'brokerage' | 'office' | ''
  >(
    featureEnabled('improvedLockboxAssignmentFeature')
      ? defaultTypeHelper()
      : '',
  );

  // When the assignment type dropdown is changed, the dropdown for the Assignee or OwnerType should reset to it's default value
  useEffect(() => {
    if (featureEnabled('improvedLockboxAssignmentFeature') || resetToDefault) {
      setType(defaultTypeHelper());
      // Also clear cached search results and selected value so the
      // keyholder search is fresh when the type changes back to default.
      setData([]);
      setSelectValue(undefined);
      // Notify parent that there is no selected value anymore.
      onValueChange(undefined);
    }
  }, [assignmentType, resetToDefault]);

  const handleSearch = async (searchTerm: string) => {
    if (
      !searchTerm ||
      (featureEnabled('improvedLockboxAssignmentFeature') && !type)
    )
      return;
    setLoading(true);
    setData([]);

    let queryString = `searchTerm=${searchTerm}&includeUserMlsId=1&includeOfficeId=1&size=10&page=0`;
    if (featureEnabled('improvedLockboxAssignmentFeature')) {
      queryString += `&type=${type}`;
    }
    const {
      data: { searchResult },
    } = await Axios.get(`/lockbox/owner_and_assignee?${queryString}`);

    setData(searchResult);
    setLoading(false);
  };

  const debounceHandleSearch = useMemo(() => debounce(handleSearch, 300), [type]);

  if (disabled === false) {
    return (
      <>
        <StyledSelectContainer style={style}>
          <StyledLabel>
            {featureEnabled('improvedLockboxAssignmentFeature')
              ? searchLabel
                ? `${searchLabel.charAt(0).toUpperCase()}${searchLabel.slice(1)}`
                : 'ID'
              : searchLabel
                ? `${searchLabel.charAt(0).toUpperCase()}${searchLabel.slice(1)} ID`
                : 'ID'}
          </StyledLabel>
          <StyledSelectGroup>
            {featureEnabled('improvedLockboxAssignmentFeature') && (
              <Select
                value={type}
                onChange={v => {
                  setType(v);
                  setData([]);
                  setSelectValue(undefined);
                  onValueChange(undefined);
                }}
                placeholder="Select type"
                style={{ width: 120 }}
                size="large"
                data-cy={`${searchLabel}-type-select`}
                options={[
                  { value: 'user', label: 'User' },
                  ...(assignmentType === 'ownerAndAssignee' &&
                  searchLabel === 'assignee'
                    ? []
                    : [{ value: 'office', label: 'Office' }]),
                ]}
              />
            )}
            <StyledSelect
              virtual={false}
              showSearch
              value={
                featureEnabled('improvedLockboxAssignmentFeature')
                  ? selectValue
                  : value
              }
              placeholder={
                featureEnabled('improvedLockboxAssignmentFeature')
                  ? 'Search'
                  : 'Select a keyholder name or ID, office name or ID'
              }
              defaultActiveFirstOption={false}
              showArrow={false}
              filterOption={false}
              onSearch={debounceHandleSearch}
              {...(featureEnabled('improvedLockboxAssignmentFeature')
                ? {
                    onChange: val => {
                      setSelectValue(val);
                      onValueChange(val);
                    },
                  }
                : {
                    onChange: onValueChange,
                  })}
              notFoundContent={loading ? <Loading /> : null}
              size="large"
              allowClear
              disabled={
                featureEnabled('improvedLockboxAssignmentFeature') && !type
              }
              options={(data || []).map((d: any) => ({
                value: `${d?.id}|${d?.type}`,
                label: SearchLabel(d, isMlsUser),
              }))}
            />
          </StyledSelectGroup>
        </StyledSelectContainer>
      </>
    );
  }

  return null;
};

const StyledSelectContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
`;

const StyledLabel = styled.label`
  margin-bottom: 8px;
  font-size: 14px;
  color: ${theme.gray8};
`;

const StyledSelect = styled(Select)`
  width: 100%;
  min-width: 280px;
  display: flex;
  position: relative;
  align-items: center;
  .ant-select-selection-item {
    padding: 0 20px !important;
  }
  .ant-select-selection-search {
    padding: 0px 25px !important;
  }
  .ant-select-selection-placeholder {
    padding: 0px 25px !important;
  }
`;

const StyledSelectGroup = styled.div`
  display: flex;
  width: 100%;
`;

export default KeyholderSearch;
